import I18n from 'i18n-js';
import { reject } from 'lodash';
import React from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { SUPPORTED_REGIONAL_SHIPPING_REGION_CODES } from '@reverbdotcom/commons/src/constants';
import { ShippingRegionSelector, IShippingRegion, ILegacyApiShippingRegion } from '@reverbdotcom/commons/src/shipping/shipping_region_selector';

import { EditShippingRateCard, FormContext } from './edit_shipping_rate_card';
import { IShippingRate } from './edit_shipping_rate_card_manual_prices';
import { core_apimessages_ShippingLabelPackage } from '../../schemas/rql';
import { MeasurementSystem, buildNewPackage } from '../discovery/shipping_labels/shipping_label_package';
import URLHelpers from './url_helpers';
import { RCTag } from '@reverbdotcom/cadence/components';
import FormSection from '@reverbdotcom/commons/src/components/form_section';

interface IProps {
  formContext: FormContext;
  shippingRates: IShippingRate[];
  currency: string;
  shippingRegionOptions: ILegacyApiShippingRegion[];
  allowedToShipExpedited: boolean;
  allowedToShipCarrierCalculated: boolean;
  inCarrierCalculatedMode: boolean;
  allowedToSetRegionalShipping: boolean;
  setIsInCarrierCalculatedMode: (boolean) => void;
  showInternationalRegions: boolean;
  shopAddressSummary?: string;
  shippingPackage?: core_apimessages_ShippingLabelPackage;
}

function handleRemoveShippingRate(shippingRateToRemove, currentShippingRates, setShippingRates) {
  const updatedShippingRates = reject(currentShippingRates, (sr) => sr.regionCode === shippingRateToRemove.regionCode);

  setShippingRates(updatedShippingRates);
}

function handleEditShippingRate(updatedShippingRate, currentShippingRates, setShippingRates) {
  const targetShippingRateIndex = currentShippingRates.findIndex((sr) => sr.regionCode === updatedShippingRate.regionCode);

  const updatedShippingRates = currentShippingRates.slice();
  updatedShippingRates[targetShippingRateIndex] = updatedShippingRate;

  setShippingRates(updatedShippingRates);
}

function buildBlankShippingRate(newShippingRegion: IShippingRegion): IShippingRate {
  const { regionType, regionCode, regionName } = newShippingRegion;

  return {
    isRequired: false,
    regionType,
    regionCode,
    regionName,
    rate: null,
    expeditedRate: null,
    incrementalRate: null,
  };
}

function EditShippingRatesFormContent({
  props,
  displayRates,
  shippingRates,
  setShippingRates,
  shippingPackage,
  setShippingPackage,
  showNewShippingRegionDropdown,
  setShowNewShippingRegionDropdown,
  newShippingRegion,
  setNewShippingRegion,
}) {
  const inShippingProfileForm = props.formContext == FormContext.SHIPPING_PROFILE;

  function allowedRegionalShipping(shippingRate) {
    return props.allowedToSetRegionalShipping &&
      inShippingProfileForm &&
      shippingRate.isRequired &&
      SUPPORTED_REGIONAL_SHIPPING_REGION_CODES.includes(shippingRate.regionCode);
  }

  function Selector() {
    return (
      <ShippingRegionSelector
        shippingRegions={props.shippingRegionOptions}
        onSelect={(r) => {
          if (r.regionCode) {
            if (inShippingProfileForm) {
              setNewShippingRegion({});
              setShippingRates(shippingRates.slice().concat(buildBlankShippingRate(r)));
            } else {
              setNewShippingRegion(r);
            }
          }
        }}
        disabledShippingRegionCodes={shippingRates.map(({ regionCode }) => regionCode)}
        value={newShippingRegion.regionCode}
      />
    );
  }

  return (
    <>
      {displayRates.map((shippingRate) => (
        <div
          className="scaling-mb-2"
          key={shippingRate.regionCode}
        >
          <EditShippingRateCard
            key={shippingRate.regionCode}
            shippingRate={shippingRate}
            handleRemoveShippingRate={(shippingRateToRemove) => handleRemoveShippingRate(shippingRateToRemove, shippingRates, setShippingRates)}
            handleEditShippingRate={(updatedShippingRate) => handleEditShippingRate(updatedShippingRate, shippingRates, setShippingRates)}
            formContext={props.formContext || FormContext.SHIPPING_PROFILE}
            currency={props.currency}
            inCarrierCalculatedMode={shippingRate.isRequired && props.inCarrierCalculatedMode}
            setIsInCarrierCalculatedMode={props.setIsInCarrierCalculatedMode}
            allowedToShipExpedited={shippingRate.isRequired && props.allowedToShipExpedited}
            allowedToShipCarrierCalculated={shippingRate.isRequired && props.allowedToShipCarrierCalculated}
            allowedRegionalShipping={allowedRegionalShipping(shippingRate)}
            shopAddressSummary={props.shopAddressSummary}
            shippingPackage={shippingPackage}
            setShippingPackage={setShippingPackage}
          />
        </div>
      ))}

      {props.showInternationalRegions && (
        <>
          {(inShippingProfileForm || showNewShippingRegionDropdown) && (
            <>
              {inShippingProfileForm && (
                <>
                  <h4 className="shipping-rate-card__title">
                    {I18n.t('discovery.dashboard.selling.shippingRates.addShippingLocationToShippingProfileHeader')}
                  </h4>
                  <div className="shipping-rate-card__main">
                    <Selector/>
                  </div>
                  <p className="shipping-rate-card-shipping-profile__help__text">
                    {I18n.t('discovery.dashboard.selling.shippingRates.addShippingLocationExplainer')}
                  </p>
                </>
              )}

              {!inShippingProfileForm && (
                <>
                  <Selector/>
                  <button
                    data-add-new-rate-for-region
                    type="button"
                    className="button button--primary"
                    onClick={() => {
                      if (newShippingRegion.regionCode) {
                        setNewShippingRegion({});
                        setShippingRates(shippingRates.slice().concat(buildBlankShippingRate(newShippingRegion)));
                      }
                    }}
                    disabled={!newShippingRegion.regionCode}
                  >
                    {I18n.t('discovery.dashboard.selling.shippingRates.addRate')}
                  </button>
                  <button
                    data-hide-regions-dropdown
                    type="button"
                    className="button-as-link ml-2"
                    onClick={() => setShowNewShippingRegionDropdown(false)}
                  >
                    {I18n.t('discovery.dashboard.selling.shippingRates.cancelAddRate')}
                  </button>
                </>
              )}
            </>
          )}

          {(!inShippingProfileForm && !showNewShippingRegionDropdown) && (
            <div className="scaling-mb-3">
              <button
                data-show-regions-dropdown
                className="button button--primary button--small icon-r-plus"
                type="button"
                onClick={() => setShowNewShippingRegionDropdown(true)}
                aria-label={I18n.t('discovery.dashboard.selling.shippingRates.addShippingLocation')}
              >
                {I18n.t('discovery.dashboard.selling.shippingRates.addShippingLocation')}
              </button>
              <p className="mt-1 size-90 lh-120 mb-0">
                {I18n.t('discovery.dashboard.selling.shippingRates.addShippingLocationExplainer')}
                <CoreLink
                  to={URLHelpers.internationalShippingGuidePath}
                  target="_blank"
                  className="ml-space"
                >
                  {I18n.t('discovery.dashboard.selling.shippingRates.learnMore')}
                </CoreLink>
              </p>
            </div>
          )}
        </>
      )}
    </>
  );
}

export function EditShippingRatesForm(props: IProps) {
  const [showNewShippingRegionDropdown, setShowNewShippingRegionDropdown] = React.useState(false);
  const [newShippingRegion, setNewShippingRegion] = React.useState<IShippingRegion>({});
  const [shippingRates, setShippingRates] = React.useState<IShippingRate[]>(props.shippingRates);
  const [shippingPackage, setShippingPackage] = React.useState(props.shippingPackage || buildNewPackage(MeasurementSystem.IMPERIAL));

  const displayRates = props.showInternationalRegions ? shippingRates : shippingRates.filter((sr) => sr.isRequired);
  const formContent = <EditShippingRatesFormContent
    props={props}
    displayRates={displayRates}
    shippingRates={shippingRates}
    setShippingRates={setShippingRates}
    shippingPackage={shippingPackage}
    setShippingPackage={setShippingPackage}
    showNewShippingRegionDropdown={showNewShippingRegionDropdown}
    setShowNewShippingRegionDropdown={setShowNewShippingRegionDropdown}
    newShippingRegion={newShippingRegion}
    setNewShippingRegion={setNewShippingRegion}
  />;

  if (props.formContext == FormContext.SHIPPING_PROFILE) {
    return (
      <FormSection
        title={I18n.t('discovery.dashboard.selling.shippingRates.rateOptionsTitleShippingProfile')}
        description={props.allowedToSetRegionalShipping ? (
          <>
            <RCTag variant="highlight">
              {I18n.t('discovery.dashboard.selling.shippingRates.newBadgeText')}
            </RCTag> {I18n.t('discovery.dashboard.selling.shippingRates.regionalOptionsAreNew')}
          </>
        ) : null}
      >
        {formContent}
      </FormSection>
    );
  }

  return (
    <div>
      <div>
        {formContent}
      </div>
    </div>
  );
}

export default EditShippingRatesForm;
